import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CircularProgress, Dialog, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import {
  studentTagAssignmentPublish,
  studentWorkMasteryAndGradeUpdate,
  useStudentTagAssignmentDetail
} from 'sdk';

import { ReactComponent as EditIcon } from 'pages/Teachers/ClassroomInsights/assets/edit-preview-icon.svg';
import { ReactComponent as PublishIcon } from 'pages/Teachers/ClassroomInsights/assets/publish-icon.svg';
import { ReactComponent as InfoIcon } from 'pages/Teachers/ClassroomInsights/assets/tomorrow-focus-icon.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import StudentWorkDialogEdit from 'pages/Teachers/ClassroomInsights/components/StudentWorkDialogEdit';
import {
  GRADES_MAPPER,
  MASTERY_LEVEL_TO_LABEL_MAPPER,
  STATUS_ICON_MAPPER
} from 'pages/Teachers/ClassroomInsights/constants';
import { shouldDisablePublish } from 'pages/Teachers/ClassroomInsights/utils';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyError, notifySuccess } from 'utils/notifications';

import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const StudentWorkDialog = ({
  currentStudent,
  objectiveValueType,
  status,
  onClose,
  onNext,
  onPrev,
  onActionSuccess,
  sectionId
}) => {
  const user = useContext(UserContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [overallMastery, setOverallMastery] = useState('');
  const [grade, setGrade] = useState('');
  const [prevId, setPrevId] = useState(null);

  const studentId = currentStudent?.student_tag_assignment_id;
  const currentOptions = GRADES_MAPPER[objectiveValueType] || [];
  const isNumberFieldRubric =
    objectiveValueType === 'custom' || objectiveValueType === 'number';

  const { data, loading, refetch } = useStudentTagAssignmentDetail({
    studentTagAssignmentId: studentId
  });
  const [isPublishDisabled, setIsPublishDisabled] = useState(false);

  const files = data?.student_works.map((student_work) => student_work.url);
  const totalPages = files?.length;
  const currentPageData = data?.student_works[currentPage];
  const match = currentOptions.find((option) => option.value === grade);
  const gradeLabel = match ? match.label : 'Label not found';

  useEffect(() => {
    if (data) {
      setOverallMastery(data.overall_mastery || '');

      const currentStudentWork = data.student_works?.[currentPage];
      setGrade(currentStudentWork?.grade);
    }
  }, [data, currentPage]);

  const handleUpdateLevels = async ({ updatedData }) => {
    const payload = {
      mastery_level: updatedData.overallMastery,
      grade: updatedData.grade
    };

    const { success } = await studentWorkMasteryAndGradeUpdate({
      studentWorkId: data?.student_works[currentPage].id,
      data: payload
    });

    if (success) {
      if (updatedData.overallMastery !== '') {
        setOverallMastery(updatedData.overallMastery);
      }

      if (updatedData.grade !== null && updatedData.grade !== undefined) {
        setGrade(updatedData.grade);
      }

      notifySuccess('Changes saved successfully!');
      onActionSuccess();
      refetch();

      setIsEdit(false);

      trackMixpanelEvent(user.id, 'Edited Mastery and Grade', {
        feature: 'live classroom insights',
        view: 'student work dialog',
        button: 'Confirm',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      });
    } else {
      notifyError('Could not save changes. Please try again.');
    }
  };

  const disablePublish = useMemo(
    () =>
      shouldDisablePublish({
        status: currentStudent?.tracker_score_student_status,
        overallResponseStatus: currentStudent?.tag_description
      }),
    [currentStudent]
  );

  if (studentId !== prevId) {
    setCurrentPage(0);
    setPrevId(studentId);
  }

  const handlePublishing = async () => {
    setIsPublishDisabled(true);
    const { success } = await studentTagAssignmentPublish({
      studentTagAssignmentId: studentId
    });

    if (success) {
      notifySuccess('Student work published successfully!');
      onActionSuccess();

      trackMixpanelEvent(user.id, 'Student work published', {
        feature: 'live classroom insights',
        view: 'student work dialog',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      });
    } else {
      notifyError('Failed to publish student work. Please try again.');
    }
  };

  const handleEditClick = () => {
    setIsEdit(true);

    trackMixpanelEvent(user.id, 'Edit Mastery and Grade Clicked', {
      feature: 'live classroom insights',
      view: 'student work dialog',
      button: 'Edit',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  return (
    <Dialog
      open={!!studentId}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { overflow: 'visible', borderRadius: '12px' } }}
    >
      {!isEdit && (
        <>
          {/* --- External Global Navigation --- */}
          <IconButton
            className={styles.globalPrev}
            onClick={() => {
              onPrev();
              setIsPublishDisabled(false);
            }}
          >
            <ChevronLeftIcon style={{ fontSize: 40, color: 'white' }} />
          </IconButton>

          <IconButton
            className={styles.globalNext}
            onClick={() => {
              onNext();
              setIsPublishDisabled(false);
            }}
          >
            <ChevronRightIcon style={{ fontSize: 40, color: 'white' }} />
          </IconButton>
        </>
      )}

      {isEdit ? (
        <StudentWorkDialogEdit
          initialMastery={overallMastery}
          initialGrade={grade}
          objectiveValueType={objectiveValueType}
          currentOptions={currentOptions}
          sectionId={sectionId}
          onConfirm={handleUpdateLevels}
          setIsEdit={setIsEdit}
        />
      ) : (
        <div className={styles.dialogContainer}>
          {/* Header */}
          <div className={styles.header}>
            <span className={styles.studentTitle}>
              {data?.student_name || 'Loading...'}
              {STATUS_ICON_MAPPER[status]}
            </span>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          {loading ? (
            <div className={styles.loader}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <div className={styles.imageSection}>
                {files?.length >= 1 && (
                  <img
                    src={currentPageData?.url}
                    alt={`${data?.student_name} Work`}
                    className={styles.mainImage}
                  />
                )}

                {/* Internal Pagination - Pages/Files for current student */}
                {totalPages > 1 && (
                  <div className={styles.paginationControls}>
                    <IconButton
                      className={styles.pageArrow}
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <span>
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <IconButton
                      className={styles.pageArrow}
                      disabled={currentPage === totalPages - 1}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </div>
                )}
              </div>

              <div className={styles.insightsSection}>
                <div className={styles.insightHeader}>
                  <div>
                    <InfoIcon />
                    <span className={styles.insightsTitle}>AI Insights</span>
                  </div>
                  {data && (
                    <EditIcon
                      onClick={() => handleEditClick()}
                      className={styles.editIcon}
                    />
                  )}
                </div>
                <div className={styles.masteryRow}>
                  <span className={styles.masteryStatus}>
                    {MASTERY_LEVEL_TO_LABEL_MAPPER[overallMastery]}
                  </span>
                  {grade !== null && grade !== undefined && (
                    <span className={styles.grade}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <CheckGradeIcon
                          grade={grade}
                          objectiveValueType={objectiveValueType}
                        />
                        {isNumberFieldRubric ? grade : gradeLabel}
                      </div>
                    </span>
                  )}
                </div>

                {currentPageData?.ai_explanation ? (
                  <p className={styles.explanation}>
                    {currentPageData?.ai_explanation}
                  </p>
                ) : (
                  <p className={styles.noExplanation}>No AI Insights found</p>
                )}
                <button
                  className={styles.publishBtn}
                  onClick={handlePublishing}
                  disabled={isPublishDisabled || disablePublish}
                >
                  <PublishIcon />
                  <span className={styles.publishBtnText}>PUBLISH</span>
                </button>
                <span className={styles.footerText}>
                  Update assignment status in the student app based on the
                  student mastery.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default StudentWorkDialog;
